import { BigNumber } from 'ethers';
import { OrderBookType, OrderMessageType } from '../../config/constants';
import { UserPositionRaw } from '../../hooks/web3/trade/useUserPositions';
import {
    generateClosePositionLog,
    isAlterPositionOrder,
    isClosePositionOrder,
    generateAdjustPositionLog,
    generateDepositPositionLog,
    generateWithdrawPositionLog,
    generateSizePositionLog,
    generatePartiallyClosePositionLog,
    generateOpenPositionLog,
} from '../index';
import moment from 'moment';
import { TransactionLog } from '../../context/transaction/transactionTypes';
import { TradePair } from '../../context/chart/chartTypes';

type GeneratePendingTransactionArgs = {
    message: OrderMessageType;
    userPosition: UserPositionRaw | undefined;
    price: string;
    orderType: OrderBookType;
    userFee: BigNumber;
    tradePair: TradePair;
    isShort: boolean;
    decimals: number;
    sigHash: string;
};

const generatePendingTransactions = ({
    message,
    userPosition,
    price,
    orderType,
    userFee,
    tradePair,
    isShort,
    decimals,
    sigHash,
}: GeneratePendingTransactionArgs): TransactionLog => {
    let leverage: string = '0',
        volume: string = '0',
        margin: string | undefined,
        fees: string | undefined,
        estimatedPnl: string | undefined,
        newMargin: string | undefined,
        newLeverage: string | undefined,
        newVolume: string | undefined;

    const { params, constraints } = message;

    if ('positionId' in params) {
        if (userPosition) {
            leverage = userPosition.leverage;
            margin = userPosition.margin;
            volume = userPosition.volume;

            if (isClosePositionOrder(orderType)) {
                ({ estimatedPnl, fees } = generateClosePositionLog(
                    userPosition,
                    price,
                    margin,
                    userFee
                ));
            }

            if (isAlterPositionOrder(orderType)) {
                if (
                    orderType === OrderBookType.EXTEND_LEVERAGE &&
                    'targetLeverage' in params
                ) {
                    ({ newMargin, newLeverage, fees } = generateAdjustPositionLog(
                        margin,
                        volume,
                        userFee,
                        params
                    ));
                }

                if (orderType === OrderBookType.ADD_MARGIN && 'addedMargin' in params) {
                    ({ newMargin, newLeverage } = generateDepositPositionLog(
                        margin,
                        volume,
                        decimals,
                        params
                    ));
                }

                if (
                    orderType === OrderBookType.REMOVE_MARGIN &&
                    'removedMargin' in params
                ) {
                    ({ newMargin, newLeverage, fees } = generateWithdrawPositionLog(
                        margin,
                        volume,
                        userFee,
                        decimals,
                        params
                    ));
                }

                if (orderType === OrderBookType.EXTEND && 'addedLeverage' in params) {
                    ({ newMargin, newVolume, newLeverage, fees } =
                        generateSizePositionLog(
                            margin,
                            volume,
                            userFee,
                            decimals,
                            params
                        ));
                }

                if (
                    orderType === OrderBookType.PARTIALLY_CLOSE &&
                    'proportion' in params
                ) {
                    ({ estimatedPnl, fees, newMargin } =
                        generatePartiallyClosePositionLog(
                            userPosition,
                            price,
                            margin,
                            userFee,
                            params
                        ));
                }
            }
        }
    } else {
        // open market orders
        ({ leverage, margin, volume, fees } = generateOpenPositionLog(
            userFee,
            decimals,
            params
        ));
    }

    const timestamp = moment
        .unix(Number(constraints.deadline.toString()))
        .subtract(1, 'minute')
        .unix();

    return {
        orderId: '',
        orderType,
        isMarketOrder: true,
        date: timestamp,
        fees,
        isShort,
        txHash: '',
        status: 'pending',
        tradePair,
        leverage,
        volume,
        margin,
        deadline: Number(constraints.deadline.toString()),
        estimatedPnl,
        newMargin,
        newLeverage,
        newVolume,
        sigHash,
    };
};

export default generatePendingTransactions;
